import * as React from "react"
import { cn } from '@/utils/cn';

const DropdownMenuContext = React.createContext<{
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

const DropdownMenu = ({
    children,
    onOpenChange
}: {
    children: React.ReactNode;
    onOpenChange?: (open: boolean) => void;
}) => {
    const [open, setOpenState] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const setOpen = React.useCallback((value: boolean | ((prev: boolean) => boolean)) => {
        setOpenState((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            if (next !== prev) onOpenChange?.(next);
            return next;
        });
    }, [onOpenChange]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, setOpen]);

    return (
        <DropdownMenuContext.Provider value={{ open, setOpen }}>
            <div className="relative inline-block text-left" ref={containerRef}>
                {children}
            </div>
        </DropdownMenuContext.Provider>
    );
};

const DropdownMenuTrigger = React.forwardRef<
    HTMLElement,
    React.HTMLAttributes<HTMLElement> & { asChild?: boolean }
>(({ className, children, asChild, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error("DropdownMenuTrigger must be used within a DropdownMenu");

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        context.setOpen(!context.open);
        if (props.onClick) props.onClick(e);
    };

    if (asChild && React.isValidElement(children)) {
        const child = children as React.ReactElement<{ className?: string; onClick?: React.MouseEventHandler<HTMLElement> } & React.RefAttributes<HTMLElement>>;
        // eslint-disable-next-line react-hooks/refs
        return React.cloneElement(child, {
            onClick: handleClick,
            ref: ref,
            ...props,
            className: cn(child.props.className, className, "cursor-pointer"),
        });
    }

    return (
        <div
            ref={ref as React.Ref<HTMLDivElement>}
            onClick={handleClick}
            className={cn("cursor-pointer", className)}
            {...props}
        >
            {children}
        </div>
    );
});
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { align?: "start" | "end" | "center"; forceMount?: boolean }
>(({ className, align = "start", forceMount, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error("DropdownMenuContent must be used within a DropdownMenu");

    if (!context.open) return null;

    const alignClasses = {
        start: "left-0",
        center: "left-1/2 -translate-x-1/2",
        end: "right-0",
    };

    return (
        <div
            ref={ref}
            className={cn(
                "absolute z-50 mt-2 min-w-[8rem] overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1 text-gray-900 dark:text-gray-100 shadow-md transition-all animate-in fade-in-0 zoom-in-95",
                alignClasses[align],
                className
            )}
            {...props}
        />
    );
});
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }
>(({ className, inset, onClick, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error("DropdownMenuItem must be used within a DropdownMenu");

    return (
        <div
            ref={ref}
            className={cn(
                "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                inset && "pl-8",
                className
            )}
            onClick={(e) => {
                onClick?.(e);
                context.setOpen(false);
            }}
            {...props}
        />
    );
});
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuLabel = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }
>(({ className, inset, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            "px-2 py-1.5 text-sm font-semibold",
            inset && "pl-8",
            className
        )}
        {...props}
    />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-gray-700", className)}
        {...props}
    />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuPortal = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuGroup = ({ children }: { children: React.ReactNode }) => <>{children}</>;

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuPortal,
    DropdownMenuGroup,
}
